import _ from 'lodash'
import { onMounted, ref, computed } from 'vue';
import { getCurrentLanguage } from '@eureka/language-tool';
import { container } from '@eureka/services';
import { BI_CHART_DIMENSIONS, BI_CHART_DIMENSIONS_OTHER } from '@/constant/chart/chart-i18n';
import { E_CHART_TYPE, E_ANALYSIS_CHART } from '@/types';

interface BIChartProps {
    configTaskFunc:() => Promise<any>,
    dataTaskFunc: () => Promise<any>,
    colorFunc?: (data: any[]) => any[],
    chartType: E_CHART_TYPE,
    isLoading: boolean,
    chartKey: E_ANALYSIS_CHART,
}

export function useBIChart(props: BIChartProps, emit: (event: 'click' | 'visualReady', ...args: any[]) => void) {
    /* data */
    const id = ref<string>('id');
    const containerClass = ref<string>(`bi-chart-${props.chartKey}__container`);
    const visualCurr = ref();
    const isChartLoaded = ref(false);
    const isEmptyChart = ref(false);

    const fieldConf = computed(() => {
        return _.map(props.chartKey === E_ANALYSIS_CHART.PAPER_TRENDS_BAR ? BI_CHART_DIMENSIONS_OTHER : BI_CHART_DIMENSIONS, cfg => ({
            code: cfg.key,
            dataType: 'TEXT',
            name: cfg.title,
            description: cfg.title,
        }));
    });

    const locale = computed(() => {
        return getCurrentLanguage();
    });

    /**
     * 用于处理bi不支持的字段
     */
    function axisFn(data: any) {
        const { datasource, datasourceDimensionConstructor } = data;
        const getDimensionsByCodes = datasource.getDimensionsByCodes;
        datasource.getDimensionsByCodes = async function(codes: string[], keys?: string[]) {
            // APD_Y在bi翻译为申请日，这边需要申请年。标当前申请人需要改成所有者
            const overwriteFields = ['APD_Y', 'ANCS', 'ADC_C'];
            // bi支持的字段内容
            const basicFieldConf = await getDimensionsByCodes.bind(datasource)(codes, keys);
            // bi支持的字段（排除不需要的）内容
            const usedBasicFieldConf = _.reject(basicFieldConf, item => _.includes(overwriteFields, item.code));
            // bi不支持的字段名
            const externalCodes = _.difference(codes, _.map(usedBasicFieldConf, 'code'));
            // bi不支持的字段内容
            const externalFields = await Promise.all(externalCodes.map(code => {
                // @ts-ignore
                return datasourceDimensionConstructor.read(_.find(fieldConf.value, { code }), locale.value)
            }));

            return [...usedBasicFieldConf, ...externalFields]
        };
    }

    function getConfig() {
        return props.configTaskFunc();
    }

    /**
     *axisFn
     * @returns 图表下载
     */
    function generateChartImage() {
        return visualCurr.value?.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
            svgSelector: '.company-bi-chart .world-cloud-wrapper',
        })
    }

    // todo: 优化
    function getData() {
        return props.dataTaskFunc().then(data => {
            return {
                ...data,
                NEW_CHART: props.chartType !== E_CHART_TYPE.GRAPH, // todo: 优化
            };
        });
    }

    async function loadVisualFromBI() {
        const [moduleVisual, moduleCreate, { default: matrixContainer }] = await Promise.all([
            import('MatrixCore/Visuals'),
            import('MatrixCore/createVisualWithoutChartId'),
            import('MatrixCore/container')
        ]);
        const { Visuals } = moduleVisual.default;
        const createVisualWithoutChartId = moduleCreate.default;
        const {
            visual,
            loadDataset,
            analysisSetting,
        } = await createVisualWithoutChartId(id.value, locale.value, {}, {
            async loadVisual(visualId:string) {
                const targetVisualChart = await import('MatrixCore/ChartInstances').then(res => {
                    return res.default[visualId]
                });
                Visuals.registerVisual(targetVisualChart);
                const ChartClass = await Visuals.install(visualId);
                return ChartClass;
            },
            patch() {
                matrixContainer.BiHttp.httpClient = container.RnDHttp.httpClient;
            },
        }, getData, getConfig, axisFn, 'Eureka');
        return {
            visual,
            loadDataset,
            analysisSetting,
        }
    }
    function clearSelect() {
        visualCurr.value?.clearSelect();
    }

    async function drawChart() {
        isEmptyChart.value = false
        const {
            visual,
            loadDataset,
            analysisSetting,
        } = await loadVisualFromBI();
        const dataset = await loadDataset('Eureka');
        //! 是否开启chart自适应，开启后小图模式，Legend会被隐藏
        visual.isChangeWithContainer = true;
        visual.clickable((data: any) => {
            emit('click', data);
        });

        visual.renderEmpty = visual.renderError = () => {
            visual.dispose();
            isEmptyChart.value = true
        };

        if (_.isFunction(props.colorFunc)) {
            visual.style.colors.colors = props.colorFunc(visual.style.colors.colors);
        }
        await visual.trueUpdate(document.querySelector(`.${containerClass.value}`), dataset, visual.displayOptions, visual.style, analysisSetting);
        visualCurr.value = visual;

        isChartLoaded.value = true;

        emit('visualReady', visualCurr.value);
    }

    onMounted(() => {
        drawChart()
    })

    return {
        visualCurr,
        containerClass,
        isChartLoaded,
        drawChart,
        generateChartImage,
        isEmptyChart,
        clearSelect,
    };
}
